import { useEffect, useRef } from 'react'
import type { ECharts } from 'echarts'
import { resolveToken } from './risk'

/**
 * Dense realtime line (ECharts, dynamically imported so it only loads on the
 * Operations screen). ECharts paints to canvas and cannot read CSS variables, so
 * colours are resolved from the token layer at mount.
 */
export function RealtimeLine({
  data,
  threshold,
  colorToken = '--flare',
  height = 220,
}: {
  data: number[]
  threshold?: number
  colorToken?: string
  height?: number
}) {
  const elRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<ECharts | null>(null)

  useEffect(() => {
    let disposed = false
    void import('echarts').then((echarts) => {
      if (disposed || !elRef.current) return
      const chart = echarts.init(elRef.current)
      chartRef.current = chart
      const color = resolveToken(colorToken)
      chart.setOption({
        grid: { left: 34, right: 12, top: 12, bottom: 20 },
        xAxis: { type: 'category', boundaryGap: false, axisLabel: { show: false }, axisLine: { show: false }, axisTick: { show: false } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: resolveToken('--border'), opacity: 0.4 } }, axisLabel: { color: resolveToken('--faint'), fontSize: 10 } },
        series: [
          {
            type: 'line',
            smooth: true,
            showSymbol: false,
            data: [],
            lineStyle: { color, width: 2 },
            areaStyle: { color, opacity: 0.14 },
            markLine:
              threshold != null
                ? { silent: true, symbol: 'none', data: [{ yAxis: threshold }], lineStyle: { color: resolveToken('--flare'), type: 'dashed' } }
                : undefined,
          },
        ],
      })
    })
    const onResize = () => chartRef.current?.resize()
    window.addEventListener('resize', onResize)
    return () => {
      disposed = true
      window.removeEventListener('resize', onResize)
      chartRef.current?.dispose()
      chartRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    chartRef.current?.setOption({
      xAxis: { data: data.map((_, i) => i) },
      series: [{ data }],
    })
  }, [data])

  return <div ref={elRef} style={{ width: '100%', height }} />
}
